// A stored design back on its layout, out of CustomizerScreen and ProcessOrderScreen
// (backfill PR 31): a design document holds only what differs from the layout and the
// keycap defaults, without the spacers, so the customizer's import of a saved design and the
// order processing's loader merge each stored keycap over the layout's keycap and the
// defaults, the loader with the keycap's row and column for the printing files; and the
// number of a design's copies a printing run holds when the layout's transfer film may be
// pooled. Since chain 9 (Oct 2026) also the loader's pass over an order: the designs, layouts
// and profiles it asks for, each once, and its designs put back on their layouts.
import { deepCopy, deepMerge } from './deepObjects';
import { defaultKeycapValues } from './designState';

export const mergeStoredKeycapData = (
  layoutRows,
  storedKeycapData,
  { withPositions = false } = {}
) =>
  layoutRows.map((row, rowIndex) => {
    let keycapIndex = 0; // tracking the index for the stored keycap (the layout's row has more objects than the stored row, because of the isSpacer objects, which are not included in the stored keycapData)
    return row.map((keycap) => {
      if (keycap.isSpacer) {
        return keycap; // Spacer key: return as is and do not increment keycapIndex
      }

      const defaultKeycap = deepCopy(defaultKeycapValues); // deep copy of default keycap values (after the spacer check since a PR 31 review: the copy was made for every spacer too)
      const designKeycap = storedKeycapData[rowIndex][keycapIndex]; // Get the corresponding keycap of the design
      keycapIndex++;

      // Merge defaultKeycap, layout keycap, and imported design keycap
      const mergedKeycap = deepMerge(
        deepMerge(defaultKeycap, keycap),
        designKeycap
      );

      if (withPositions) {
        // give it row/col identity for the printing files (the order screen's copy also checked the merged keycap for the spacer flag, which a stored keycap never carries: dropped after a PR 31 review):
        mergedKeycap.rowIndex = rowIndex;
        mergedKeycap.colIndex = keycapIndex - 1; // (since we did ++ above, keycapIndex is now one past the actual colIndex)
      }

      return mergedKeycap;
    });
  }); // the design's keycap rows as the screens hold them: the layout's rows with each stored keycap merged over the layout's keycap and the keycap defaults

export const printingCopies = (layoutDoc, orderItem) => {
  const allowPooling = layoutDoc?.transferFilm?.allowFilmPooling === true; // check if layout has transfer film pooling enabled

  const copies = allowPooling ? Number(orderItem?.quantity) || 1 : 1; // if transfer film pooling is allowed, get the orderItem.quantity of this design (so that it can be use inside the assignKeycapsToMolds function)

  return { poolingEnabled: allowPooling, copies };
}; // how many copies of the design the mold assignment lays out at once: the order item's quantity when the layout's transfer film may be pooled, else one

export const uniqueDesignIds = (orderItems) => [
  ...new Set(
    orderItems.filter((item) => item.designId).map((item) => item.designId)
  ),
]; // the designs an order's items reference, each once, for POST /api/designs/multiple (an item without a design is skipped: the schema leaves the field optional, the checkout gives every item its design). This and the two lists below stay three one-liners, each named for its request, rather than one generic helper (a chain 9 review)

export const uniqueLayoutIds = (designs) => [
  ...new Set(designs.map((design) => design.layoutEditor.id)),
]; // the layouts the loaded designs sit on, each once, for POST /api/layouts/multiple; read as designsOnLayouts reads it, bare, as the screen's merge did before chain 9 (a guard and a filter for a design without one protected nothing and went, a review). The model has required it since the Dec 2024 schema, which replaced a top-level layout reference, and no document from before is left (checked by query, Oct 2026)

export const uniqueProfiles = (designs) => [
  ...new Set(designs.map((design) => design.profileEditor.profile)),
]; // the keycap profiles the loaded designs use, each once: one molds request per profile. Every stored design has one: the model has required it since the Dec 2024 schema, and no document from before is left (checked by query, Oct 2026), so a guard and a filter went in chain 9 with designWithPrintingRuns' branch for a design without a profile (user decision)

export const designsOnLayouts = (designs, layouts) =>
  designs.map((design) => {
    const layoutDocument = layouts.find(
      (layout) => layout._id === design.layoutEditor.id
    ); // find the layout for each design

    if (!layoutDocument) {
      return design; // No layout found for this design - just return the design as is
    }

    // Now merge each design's keycapData with the corresponding layout:
    const keycapData = mergeStoredKeycapData(
      layoutDocument.layout,
      design.keycapData,
      { withPositions: true }
    ); // each stored keycap merged over the layout's keycap and the keycap defaults, with the layout's spacers put back and its row/col identity for the printing files

    return { ...design, keycapData: keycapData };
  }); // an order's designs put back on their layouts for the printing files; a design whose layout the list lacks keeps its stored keycaps (no route deletes a layout, but an admin can in the database; the layouts route then leaves it out, and the keycaps, without the layout's sizes, fit no mold slot, so the screen warns of unassigned keycaps for it). A find per design: an order holds a handful of designs and layouts, and the loader's lookup by id serves the printing runs after this merge, as the screen had it (left so, a chain 9 review)
